import { useEffect, type ReactNode } from "react";
import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { MessageKey } from "@expertloop/i18n";
import { conversationElementId, EVENT_STYLE, type ConversationItem } from "./conversation";
import { revealInConversation } from "./dossier";
import { MediaLinks } from "./MediaLinks";
import type { AdminMediaFile, AdminMediaVideo } from "@expertloop/shared";
import type { TextKey } from "./translations";

export interface ConversationFocus {
	ref: string;
	/** Change à chaque clic : un second clic sur la même source ramène l'élément à l'écran. */
	nonce: number;
}

interface MediationConversationPanelProps {
	items: ConversationItem[];
	currency: string;
	focus: ConversationFocus | null;
	newMessageIds: Set<string>;
	newDeliveryIds: Set<string>;
	/** Texte dans la langue de consultation, avec sa mention de traduction. */
	renderText: (key: TextKey, original: unknown) => { text: string; note: ReactNode };
	/** Ouverture des pièces dans le lecteur. */
	media: { busyId: string | null; onOpenFile: (file: AdminMediaFile) => void; onOpenVideo: (video: AdminMediaVideo) => void };
	/** Texte d'un événement (motif d'ouverture, message de décision), quand il en porte un. */
	eventText: (ref: string) => { label: MessageKey; text: string; note: ReactNode } | null;
}

/**
 * Fil de la médiation tel que le voient les deux parties, événements compris (MEDIATIONUX-3). Les
 * sources du rapport y amènent l'élément cité, mis en valeur par une barre colorée et du gras.
 */
export function MediationConversationPanel({ items, currency, focus, newMessageIds, newDeliveryIds, renderText, eventText, media }: MediationConversationPanelProps) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();

	useEffect(() => {
		if (!focus) return;
		const element = document.getElementById(conversationElementId(focus.ref));
		if (element) revealInConversation(element);
	}, [focus]);

	const focusedId = focus ? conversationElementId(focus.ref) : null;
	const classFor = (base: string, id: string) => `${base}${id === focusedId ? " conversation-item--focus" : ""}`;

	return (
		<section className="card mediation-conversation" id="mediation-messages" aria-label={t("adminChatDetail.conversation.title")}>
			<div className="card-header">
				<div>
					<h2>{t("adminChatDetail.conversation.title")}</h2>
					<p className="translation-indicative">{t("adminChatDetail.conversation.subtitle")}</p>
				</div>
			</div>
			<div className="card-body">
				{items.length === 0 ? (
					<p>{t("adminChatDetail.conversation.empty")}</p>
				) : (
					<ol className="conversation-list">
						{items.map((item) => {
							const id = conversationElementId(item.ref);
							const focused = id === focusedId;
							const marker = focused ? <span className="mediation-chip">{t("adminChatDetail.conversation.selected")}</span> : null;

							if (item.kind === "request" || item.kind === "message") {
								const key: TextKey =
									item.kind === "request"
										? { objectType: "chat_request", objectId: item.chatId, field: "requestMessage" }
										: { objectType: "message", objectId: item.messageId, field: "content" };
								const { text, note } = renderText(key, item.content);
								const sender = item.kind === "request" ? "client" : item.sender;
								return (
									<li key={item.ref} id={id} className={classFor(`conversation-item conversation-message conversation-${sender}`, id)} aria-current={focused ? "true" : undefined}>
										<div className="conversation-meta">
											<strong>{item.kind === "request" ? t("adminChatDetail.conversation.request") : t(`adminChatDetail.dossier.timeline.actor.${sender}` as MessageKey)}</strong>
											<span>{new Date(item.at).toLocaleString()}</span>
											{item.kind === "message" && newMessageIds.has(item.messageId) && <span className="new-badge">{t("adminChatDetail.mediationCase.newBadge")}</span>}
											{marker}
										</div>
										<div className="conversation-text">
											{text}
											{note}
										</div>
										{item.kind === "message" && (
											<MediaLinks files={item.files} videos={item.videos} busyId={media.busyId} onOpenFile={media.onOpenFile} onOpenVideo={media.onOpenVideo} />
										)}
									</li>
								);
							}

							if (item.kind === "delivery") {
								const message = item.content ? renderText({ objectType: "delivery", objectId: item.deliveryId, field: "message" }, item.content) : null;
								const rejection = item.rejectionReason ? renderText({ objectType: "delivery", objectId: item.deliveryId, field: "rejectionReason" }, item.rejectionReason) : null;
								return (
									<li key={item.ref} id={id} className={classFor("conversation-item conversation-delivery", id)} aria-current={focused ? "true" : undefined}>
										<div className="conversation-meta">
											<strong>{t("adminChatDetail.conversation.delivery")}</strong>
											<span>{new Date(item.at).toLocaleString()}</span>
											{newDeliveryIds.has(item.deliveryId) && <span className="new-badge">{t("adminChatDetail.mediationCase.newBadge")}</span>}
											{marker}
										</div>
										{message && (
											<div className="conversation-text">
												{message.text}
												{message.note}
											</div>
										)}
										{item.amount !== undefined && <div className="conversation-meta">{t("adminChatDetail.conversation.deliveryAmount", { amount: formatCents(item.amount, currency) })}</div>}
										<MediaLinks
											files={[...item.files, ...item.previewFiles]}
											videos={item.videos}
											busyId={media.busyId}
											onOpenFile={media.onOpenFile}
											onOpenVideo={media.onOpenVideo}
										/>
										{rejection && (
											<div className="conversation-text">
												<strong>{t("adminChatDetail.conversation.rejectionReason")} : </strong>
												{rejection.text}
												{rejection.note}
											</div>
										)}
									</li>
								);
							}

							const extra = eventText(item.ref);
							return (
								<li key={item.ref} id={id} className={classFor(`conversation-item conversation-event conversation-event--${EVENT_STYLE[item.event.type].tone}`, id)} aria-current={focused ? "true" : undefined}>
									<div className="conversation-meta">
										<i className={`fas ${EVENT_STYLE[item.event.type].icon}`} aria-hidden="true"></i>
										<strong>{t(`adminChatDetail.dossier.timeline.type.${item.event.type}` as MessageKey)}</strong>
										<span>{t(`adminChatDetail.dossier.timeline.actor.${item.event.actor}` as MessageKey)}</span>
										{item.event.amount !== undefined && <span>{formatCents(item.event.amount, item.event.currency ?? currency)}</span>}
										<span>{new Date(item.at).toLocaleString()}</span>
										{marker}
									</div>
									{extra && (
										<div className="conversation-text">
											<em>{t(extra.label)} : </em>
											{extra.text}
											{extra.note}
										</div>
									)}
								</li>
							);
						})}
					</ol>
				)}
			</div>
		</section>
	);
}
